import isPropValid from "@emotion/is-prop-valid";
import styled from "@emotion/styled";
import { Stack } from "@mui/material";
import moment from "moment/moment";
import EtDatePicker from "mui-ethiopian-datepicker";
import { forwardRef } from "react";

// Props: height (number|string), borderRadius (number|string)
const StyledDateField = styled(EtDatePicker, {
  shouldForwardProp: prop =>
    isPropValid(prop) && prop !== "height" && prop !== "borderRadius"
})(({ theme, height, borderRadius }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: typeof borderRadius !== "undefined" ? borderRadius : "4px",
    backgroundColor: theme.palette.background.paper,
    borderColor: "rgb(179, 179, 179)",
    outline: "none",
    ...(typeof height !== "undefined" && height !== null && height !== ""
      ? { minHeight: height, height }
      : {}),
    "& fieldset": {
      borderColor: "rgb(179, 179, 179)",
      borderWidth: "0.5px"
    },
    "&:hover fieldset": {
      borderColor: "rgb(179, 179, 179)",
      borderWidth: "0.5px"
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgb(179, 179, 179)",
      borderWidth: "0.5px"
    }
  },
  ...(typeof height !== "undefined" && height !== null && height !== ""
    ? {
        "& .MuiInputBase-input": {
          minHeight: height,
          height,
          boxSizing: "border-box",
          padding: "16.5px 14px"
        }
      }
    : {}),
  "& .MuiInputBase-root.Mui-disabled": {
    backgroundColor: "rgb(255, 255, 255)",
    borderColor: "rgb(179, 179, 179)",
    outline: "none",
    "& fieldset": {
      borderColor: "rgb(179, 179, 179)",
      borderWidth: "0.5px"
    }
  }
}));

/**
 * EthiopianDatePicker
 * Props:
 *   - height: number|string (input height, optional)
 *   - borderRadius: number|string (input border radius, optional)
 *
 * Backward compatible: If height or borderRadius are not provided, defaults are used (height: native/input default, borderRadius: 4px)
 *
 * Example usage:
 *   <EthiopianDatePicker ... /> // default
 *   <EthiopianDatePicker height={56} borderRadius={12} ... /> // custom
 */
export const EthiopianDatePicker = forwardRef(
  (
    {
      label,
      width = "100%",
      height = undefined,
      borderRadius = undefined,
      value,
      onChange,
      ...rest
    },
    ref
  ) => {
    // Pass the date string directly to EtDatePicker
    const dateValue = value ? moment(value).toDate() : null;

    const handleChange = newDate => {
      if (newDate) {
        onChange({ target: { value: moment(newDate).format("YYYY-MM-DD") } });
      } else {
        // Emit null (not "") on clear. yup's .date().nullable() accepts null
        // but tries to cast "" to a Date, which fails with "Invalid Date".
        onChange({ target: { value: null } });
      }
    };

    return (
      <Stack width={width} sx={height ? { height } : undefined}>
        <StyledDateField
          size="small"
          label={label}
          InputLabelProps={{ shrink: true }}
          inputRef={ref}
          value={dateValue}
          onChange={handleChange}
          height={height}
          borderRadius={borderRadius}
          key={value || "empty"}
          {...rest}
        />
      </Stack>
    );
  }
);

EthiopianDatePicker.displayName = "EthiopianDatePicker";
